import { format } from 'date-fns'
import { motion } from 'motion/react'
import React, { useEffect, useRef, useState } from 'react'
import { Icon } from '#app/components/ui/icon.tsx'
import { useDateFnsLocale } from '#app/utils/i18n.tsx'
import { cn } from '#app/utils/misc.tsx'

interface GalleryItem {
	title: string
	subtitle: string
	image: string
	link?: string
}

interface HorizontalScrollGalleryProps {
	items: GalleryItem[]
	className?: string
}

const HorizontalScrollGallery: React.FC<HorizontalScrollGalleryProps> = ({
	items,
	className = 'container overflow-x-hidden max-w-[1108px]',
}) => {
	const scrollRef = useRef<HTMLDivElement>(null)
	const [currentProgress, setCurrentProgress] = useState({
		current: 0,
		total: items.length,
	})
	const [canScrollLeft, setCanScrollLeft] = useState(false)
	const [canScrollRight, setCanScrollRight] = useState(true)

	const calculateVisibleItems = () => {
		if (!scrollRef.current) return 1

		const container = scrollRef.current
		const containerWidth = container.clientWidth
		const scrollLeft = container.scrollLeft
		const itemWidth = container.scrollWidth / items.length

		// Calculate how many items fit in the viewport
		const itemsPerView = Math.floor(containerWidth / itemWidth)

		// Calculate which item is at the rightmost visible position
		const scrollProgress = scrollLeft / (container.scrollWidth - containerWidth)
		const maxVisibleIndex = Math.min(
			Math.ceil(scrollProgress * (items.length - itemsPerView)) + itemsPerView,
			items.length,
		)

		return Math.max(itemsPerView, maxVisibleIndex)
	}

	const updateScrollState = () => {
		if (!scrollRef.current) return

		const container = scrollRef.current
		const visibleItems = calculateVisibleItems()

		setCurrentProgress({ current: visibleItems, total: items.length })

		// Update chevron button states
		setCanScrollLeft(container.scrollLeft > 0)
		setCanScrollRight(
			container.scrollLeft < container.scrollWidth - container.clientWidth - 1,
		)
	}

	const scrollLeft = () => {
		if (!scrollRef.current) return
		const container = scrollRef.current
		const itemWidth = container.scrollWidth / items.length
		container.scrollBy({ left: -itemWidth, behavior: 'smooth' })
	}

	const scrollRight = () => {
		if (!scrollRef.current) return
		const container = scrollRef.current
		const itemWidth = container.scrollWidth / items.length
		container.scrollBy({ left: itemWidth, behavior: 'smooth' })
	}

	useEffect(() => {
		const container = scrollRef.current
		if (!container) return

		let measurementCount = 0
		const maxMeasurements = 250 // Prevent infinite loops

		const resizeObserver = new ResizeObserver(() => {
			if (measurementCount >= maxMeasurements) return
			measurementCount++

			setTimeout(() => {
				updateScrollState()
			}, 100)
		})

		resizeObserver.observe(container)
		container.addEventListener('scroll', updateScrollState)
		window.addEventListener('resize', updateScrollState)

		// Set initial state
		updateScrollState()

		return () => {
			container.removeEventListener('scroll', updateScrollState)
			window.removeEventListener('resize', updateScrollState)
			resizeObserver.disconnect()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items.length])

	const renderItem = (item: GalleryItem, index: number) => {
		const content = (
			<div className="w-full max-w-[240px] flex-shrink-0">
				<div className="flex h-[400px] flex-col gap-y-4 overflow-hidden">
					<div className="flex-shrink-0">
						<h3 className="mb-0.5 line-clamp-1 text-body-2xs font-medium uppercase tracking-[0.2em] text-current">
							{item.title}
						</h3>
						<p className="line-clamp-1 text-body-2xs font-extralight tracking-[0.2em] text-current">
							/ {item.subtitle} /
						</p>
					</div>
					<div className="relative flex-grow">
						{item.link && (
							<Icon
								size={'lg'}
								name={'external-link'}
								className={cn(
									'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
									'opacity-0 transition-all duration-300 ease-in group-hover:opacity-100',
								)}
							/>
						)}
						<img
							src={item.image}
							alt={item.title}
							className="h-full w-full object-fill transition-all duration-200 group-hover:opacity-40 group-hover:brightness-75"
						/>
					</div>
				</div>
			</div>
		)

		if (item.link) {
			return (
				<motion.a
					key={index}
					href={item.link}
					whileHover={{
						scale: 0.99,
						transition: {
							duration: 0.2,
						},
					}}
					className="group relative block w-full"
				>
					{content}
				</motion.a>
			)
		}

		return <div key={index}>{content}</div>
	}

	return (
		<div className={`w-full ${className}`}>
			{/* Scrollable container */}
			<div
				ref={scrollRef}
				className={`grid snap-x snap-mandatory auto-cols-max grid-flow-col grid-rows-1 gap-x-6 overflow-x-auto scroll-smooth pb-8`}
				style={{
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
					// @ts-ignore
					WebkitScrollbar: { display: 'none' },
				}}
			>
				{items.map((item, index) => (
					<div key={index} className="snap-center">
						{renderItem(item, index)}
					</div>
				))}
			</div>

			{/* Progress indicator with navigation */}
			{Number.isNaN(currentProgress.current) ? null : (
				<div className="flex items-center justify-between">
					<div className="text-body-xs font-extralight text-current">
						{currentProgress.current}/{currentProgress.total}
					</div>

					{/* Progress bar */}
					<div className="ml-8 mr-3 flex-1">
						<div className="h-0.5 overflow-hidden rounded-full bg-current opacity-70 invert">
							<div
								className="h-full bg-current invert transition-all duration-300 ease-out"
								style={{
									width: `${(currentProgress.current / currentProgress.total) * 100}%`,
								}}
							/>
						</div>
					</div>

					{/* Navigation buttons */}
					<div className="flex items-center gap-2">
						<button
							onClick={scrollLeft}
							disabled={!canScrollLeft}
							className={cn(
								'transition-opacity duration-200',
								canScrollLeft
									? 'cursor-pointer opacity-100 hover:opacity-70'
									: 'cursor-not-allowed opacity-30',
							)}
							aria-label="Scroll left"
						>
							<Icon name="lucide-chevron-left" size="md" />
						</button>
						<button
							onClick={scrollRight}
							disabled={!canScrollRight}
							className={cn(
								'transition-opacity duration-200',
								canScrollRight
									? 'cursor-pointer opacity-100 hover:opacity-70'
									: 'cursor-not-allowed opacity-30',
							)}
							aria-label="Scroll right"
						>
							<Icon name="lucide-chevron-right" size="md" />
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

// Example usage with sample data
const MagazinePressGallery = () => {
	const locale = useDateFnsLocale()

	const sampleItems: GalleryItem[] = [
		{
			title: 'MANERA',
			subtitle: format(new Date(2025, 5), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/1_Manera.webp',
			link: 'https://maneramagazine.es/diseno/sapienstone-y-jaime-beriestain-revisten-el-bano-del-lujo-de-la-alta-costura/',
		},
		{
			title: 'AD SPAIN',
			subtitle: format(new Date(2025, 4), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/2-ad-espana.webp',
			link: 'https://www.revistaad.es/diseno/branded/galerias/dress-your-code-la-coleccion-de-jaime-beriestain-para-sapienstone',
		},
		{
			title: 'ROOM DISEÑO',
			subtitle: format(new Date(2025, 4), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/3-room-diseno.webp',
			link: 'https://roomdiseno.com/dress-code-sapienstone/',
		},
		{
			title: 'Living',
			subtitle: format(new Date(2025, 3), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/4_Living.webp',
			link: 'https://www.instagram.com/p/DIRDQJjov59/',
		},
		{
			title: 'ambiente cucina',
			subtitle: format(new Date(2025, 1), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/5_Ambiente cucina.webp',
		},
		{
			title: 'MANERA',

			subtitle: format(new Date(2025, 0), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/6_Manera.webp',
			link: 'https://maneramagazine.es/interiorismo/una-superficie-ceramica-con-tecnologia-4d-sapienstone-demuestra-que-es-posible/',
		},
		{
			title: 'LIFESTYLE PLUS',
			subtitle: format(new Date(2024, 10), 'MMM yyyy', {
				locale,
			}),
			image: '/img/magazine/press/7_Livingetc.webp',
			link: 'https://www.livingetc.com/features/sapienstone-new-designs',
		},
	]

	return <HorizontalScrollGallery items={sampleItems} />
}

export { HorizontalScrollGallery }

export default MagazinePressGallery
